import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { consolidateBreakdown, type BreakdownItem } from './breakdownConsolidation'
import { computeCargoMetrics } from './cargoMetrics'
import { FEE_UNIT_MAP, computeFeeSubtotal } from '../constants/feeUnits'

export interface OfferExportData {
  id: string
  status: string
  currency: string
  totalValue: number
  totalValueBRL?: number | null
  totalValueUSD?: number | null
  totalsByCurrency?: Record<string, number> | null
  freeTimeDays?: number | null
  carrierName?: string | null
  routeType?: 'direct' | 'transshipment' | null
  routeLegs?: Array<{ port: string; country: string; eta?: string | null; etd?: string | null }> | null
  inputCurrency?: string | null
  exchangeRateUsed?: number | null
  exchangeRateSource?: string | null
  breakdown?: BreakdownItem[] | Record<string, number> | null
  paymentTerms?: string | null
  deliveryTime?: number | null
  validityDate?: string | null
  notes?: string | null
  conditions?: Array<{ id: string; title: string; content: string }> | null
  createdAt: string

  // Freight forwarder
  freightForwarderId: string
  agentName: string
  agentTradingName?: string | null
  agentLogoUrl?: string | null
  agentCnpj?: string | null
  agentAvgRating?: number
  agentReviewCount?: number
  agentCompletedCount?: number

  // Quote / shipper
  quoteId: string
  quoteTxControl: string
  shipperName?: string | null
  shipperTradingName?: string | null
  shipperCnpj?: string | null
  origin?: {
    location: { city: string; state?: string; country: string }
  } | null
  destination?: {
    location: { city: string; state?: string; country: string }
  } | null
  incoterm?: string | null
  modalityName?: string | null
  modalityType?: string | null
  volumeName?: string | null
  goodsDescription?: string | null
  goodsNcm?: string | null
  goodsNcmList?: string[] | null
  volumeItems?: Array<{
    type: string
    quantity: number
    weight: number
    dimensions?: { length: number; width: number; height: number; unit: string }
    ncm?: string | null
  }> | null
}

const MODALITY_LABELS: Record<string, string> = {
  maritimo: 'Marítimo',
  aereo: 'Aéreo',
  terrestre: 'Terrestre'
}

const OPERATION_LABELS: Record<string, string> = {
  import: 'Importação',
  export: 'Exportação'
}

const CURRENCY_LABELS: Record<string, string> = {
  BRL: 'R$',
  USD: 'US$',
  EUR: '€',
  GBP: '£',
  CNY: '¥'
}

function formatDate(d?: string | null): string {
  if (!d) return '-'
  try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
}

function formatLocation(loc?: { city: string; state?: string; country: string }): string {
  if (!loc) return '-'
  const parts = [loc.city]
  if (loc.state) parts.push(loc.state)
  parts.push(loc.country)
  return parts.join(', ')
}

function formatCurrency(value: number, currency: string): string {
  const symbol = CURRENCY_LABELS[currency] || currency
  const locale = currency === 'BRL' ? 'pt-BR' : 'en-US'
  const formatted = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)
  return `${symbol} ${formatted}`
}

function formatWeight(w: number): string {
  if (w >= 1000) return `${(w / 1000).toFixed(2)} ton`
  return `${w} kg`
}

function getInitials(name: string): string {
  return (name || 'E').split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2)
}

async function imageToBase64(url: string): Promise<string | null> {
  if (!url) return null
  try {
    const res = await fetch(url, { mode: 'cors' })
    if (!res.ok) return null
    const blob = await res.blob()
    if (!blob.type.startsWith('image/')) return null
    return new Promise((resolve) => {
      const r = new FileReader()
      r.onloadend = () => resolve(r.result as string)
      r.onerror = () => resolve(null)
      r.readAsDataURL(blob)
    })
  } catch { return null }
}

function createPdfHtml(offer: OfferExportData, agentLogo: string | null): string {
  const initials = getInitials(offer.agentTradingName || offer.agentName)
  const agentLogoHtml = agentLogo
    ? `<img src="${agentLogo}" style="width: 56px; height: 56px; border-radius: 8px; object-fit: cover;" />`
    : `<div style="width: 56px; height: 56px; background: linear-gradient(135deg, #059669, #047857); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: bold;">${initials}</div>`

  // Breakdown consolidated with per-fee unit + subtotal
  let breakdownHtml = ''
  if (Array.isArray(offer.breakdown) && offer.breakdown.length > 0) {
    const cargo = computeCargoMetrics(
      Array.isArray(offer.volumeItems) ? offer.volumeItems : [],
      offer.volumeName
    )
    const consolidated = consolidateBreakdown(offer.breakdown as BreakdownItem[])
    breakdownHtml = `
      <div style="margin-top: 14px;">
        <div style="font-size: 11px; color: #6B7280; margin-bottom: 6px; font-weight: 600;">COMPOSIÇÃO DA TARIFA</div>
        <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
          <thead>
            <tr style="background: #F3F4F6;">
              <th style="text-align: left; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Descrição</th>
              <th style="text-align: left; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Unidade</th>
              <th style="text-align: right; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Tarifa</th>
              <th style="text-align: right; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${consolidated.map(item => {
              const unit = (item as any).unit || 'FORFAIT'
              const def = FEE_UNIT_MAP[unit as keyof typeof FEE_UNIT_MAP]
              const rate = Number(item.value) || 0
              const subtotal = def ? computeFeeSubtotal(rate, unit, cargo) : rate
              const itemCurrency = item.currency || offer.currency || 'USD'
              return `
                <tr>
                  <td style="padding: 6px 8px; border-bottom: 1px solid #F3F4F6;">${item.name}</td>
                  <td style="padding: 6px 8px; border-bottom: 1px solid #F3F4F6; color: #6B7280;">${def ? def.label : '—'}</td>
                  <td style="text-align: right; padding: 6px 8px; border-bottom: 1px solid #F3F4F6; font-variant-numeric: tabular-nums;">${formatCurrency(rate, itemCurrency)}${def && unit !== 'FORFAIT' ? `<span style="color:#9CA3AF; font-size:10px;">${def.shortLabel}</span>` : ''}</td>
                  <td style="text-align: right; padding: 6px 8px; border-bottom: 1px solid #F3F4F6; font-variant-numeric: tabular-nums; font-weight: 600;">${formatCurrency(subtotal, itemCurrency)}</td>
                </tr>
              `
            }).join('')}
          </tbody>
        </table>
      </div>
    `
  }

  // Volumes table
  let volumesHtml = ''
  if (offer.volumeItems && offer.volumeItems.length > 0) {
    const totalQty = offer.volumeItems.reduce((s, i) => s + (i.quantity || 0), 0)
    const totalWeight = offer.volumeItems.reduce((s, i) => s + (i.weight * i.quantity || 0), 0)
    volumesHtml = `
      <div style="margin-top: 14px;">
        <div style="font-size: 11px; color: #6B7280; margin-bottom: 6px; font-weight: 600;">CARGA / VOLUMES</div>
        <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
          <thead>
            <tr style="background: #F3F4F6;">
              <th style="text-align: left; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Tipo</th>
              <th style="text-align: center; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Qtd</th>
              <th style="text-align: right; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Peso</th>
              <th style="text-align: center; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">Dimensões</th>
              <th style="text-align: left; padding: 6px 8px; border-bottom: 1px solid #E5E7EB;">NCM</th>
            </tr>
          </thead>
          <tbody>
            ${offer.volumeItems.map(item => `
              <tr>
                <td style="padding: 6px 8px; border-bottom: 1px solid #F3F4F6;">${item.type}</td>
                <td style="text-align: center; padding: 6px 8px; border-bottom: 1px solid #F3F4F6;">${item.quantity}</td>
                <td style="text-align: right; padding: 6px 8px; border-bottom: 1px solid #F3F4F6;">${formatWeight(item.weight)}</td>
                <td style="text-align: center; padding: 6px 8px; border-bottom: 1px solid #F3F4F6;">${
                  item.dimensions ? `${item.dimensions.length}x${item.dimensions.width}x${item.dimensions.height} ${item.dimensions.unit}` : '-'
                }</td>
                <td style="padding: 6px 8px; border-bottom: 1px solid #F3F4F6; font-family: monospace; font-size: 10px;">${item.ncm || '-'}</td>
              </tr>
            `).join('')}
            <tr style="background: #F3F4F6; font-weight: 600;">
              <td style="padding: 6px 8px;">Total</td>
              <td style="text-align: center; padding: 6px 8px;">${totalQty}</td>
              <td style="text-align: right; padding: 6px 8px;">${formatWeight(totalWeight)}</td>
              <td></td><td></td>
            </tr>
          </tbody>
        </table>
      </div>
    `
  }

  // Modality chips
  const modalityChips: string[] = []
  if (offer.modalityName) modalityChips.push(MODALITY_LABELS[offer.modalityName] || offer.modalityName)
  if (offer.modalityType) modalityChips.push(OPERATION_LABELS[offer.modalityType] || offer.modalityType)
  if (offer.volumeName) modalityChips.push(offer.volumeName)
  if (offer.incoterm) modalityChips.push(offer.incoterm)

  // NCM list
  const ncms = (offer.goodsNcmList && offer.goodsNcmList.length > 0)
    ? offer.goodsNcmList.join(', ')
    : (offer.goodsNcm || '')

  // Build per-currency totals. Prefer totalsByCurrency; fall back to legacy
  // totalValueUSD/BRL or totalValue/currency for older offers.
  const CURRENCY_ORDER = ['USD', 'BRL', 'EUR', 'GBP', 'CNY']
  let totalEntries: Array<[string, number]> = []
  if (offer.totalsByCurrency && typeof offer.totalsByCurrency === 'object' && Object.keys(offer.totalsByCurrency).length > 0) {
    totalEntries = CURRENCY_ORDER
      .filter((c) => Number(offer.totalsByCurrency![c]) > 0)
      .map((c) => [c, Number(offer.totalsByCurrency![c])])
  } else {
    const usd = Number(offer.totalValueUSD)
    const brl = Number(offer.totalValueBRL)
    if (usd > 0) totalEntries.push(['USD', usd])
    if (brl > 0) totalEntries.push(['BRL', brl])
    if (totalEntries.length === 0 && Number(offer.totalValue) > 0) {
      totalEntries.push([offer.currency || 'USD', Number(offer.totalValue)])
    }
  }

  const totalsHtml = `
    <div style="margin-top: 16px; background: #ECFDF5; border: 1px solid #D1FAE5; border-radius: 8px; padding: 14px;">
      <div style="font-size: 10px; color: #047857; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 8px;">TOTAL DA PROPOSTA</div>
      ${totalEntries.map(([c, v], idx) => `
        <div style="display: flex; justify-content: space-between; align-items: center; ${idx > 0 ? 'margin-top: 4px; padding-top: 4px; border-top: 1px solid #D1FAE5;' : ''}">
          <div style="font-size: 11px; color: #065F46; font-weight: 600;">${c}</div>
          <div style="font-size: ${idx === 0 ? '18px' : '14px'}; font-weight: bold; color: #047857;">${formatCurrency(v, c)}</div>
        </div>
      `).join('')}
    </div>
  `

  // Routing (maritime-only fields, render only when populated)
  const routingHasData = offer.carrierName || offer.freeTimeDays != null || offer.routeType
  const legsHtml = (offer.routeType === 'transshipment' && offer.routeLegs && offer.routeLegs.length > 0)
    ? `<div style="margin-top: 6px; padding: 8px; background: #F9FAFB; border-radius: 6px;">
         <div style="font-size: 10px; color: #6B7280; margin-bottom: 4px;">Portos no caminho</div>
         <div style="font-size: 11px; color: #111827;">
           ${offer.routeLegs.map((l, i) => `
             <span><strong>${l.port}</strong>${l.country ? `, ${l.country}` : ''}${(l.eta || l.etd) ? ` <span style="color:#6B7280;">(${l.eta ? `ETA ${formatDate(l.eta)}` : ''}${l.eta && l.etd ? ' · ' : ''}${l.etd ? `ETD ${formatDate(l.etd)}` : ''})</span>` : ''}</span>${i < offer.routeLegs!.length - 1 ? ' &rarr; ' : ''}
           `).join('')}
         </div>
       </div>`
    : ''
  const routingHtml = routingHasData ? `
    <div style="margin-top: 14px;">
      <div style="font-size: 11px; color: #6B7280; margin-bottom: 6px; font-weight: 600;">ROTA E ARMADOR</div>
      <div style="display: flex; gap: 16px; font-size: 11px;">
        ${offer.carrierName ? `<div><span style="color:#6B7280;">Armador:</span> <strong>${offer.carrierName}</strong></div>` : ''}
        ${offer.freeTimeDays != null ? `<div><span style="color:#6B7280;">Free Time:</span> <strong>${offer.freeTimeDays} dias</strong></div>` : ''}
        ${offer.routeType ? `<div><span style="color:#6B7280;">Rota:</span> <strong>${offer.routeType === 'direct' ? 'Direta' : 'Com transbordo'}</strong></div>` : ''}
      </div>
      ${legsHtml}
    </div>
  ` : ''

  // Conditions
  const conditionsHtml = offer.conditions && offer.conditions.length > 0 ? `
    <div style="margin-top: 14px;">
      <div style="font-size: 11px; color: #6B7280; margin-bottom: 6px; font-weight: 600;">CONDIÇÕES</div>
      ${offer.conditions.map(c => `
        <div style="margin-bottom: 8px;">
          <div style="font-size: 11px; font-weight: 600; color: #111827;">${c.title}</div>
          <div style="font-size: 10px; color: #4B5563; white-space: pre-wrap; line-height: 1.4;">${c.content}</div>
        </div>
      `).join('')}
    </div>
  ` : ''

  return `
    <div style="font-family: 'Segoe UI', Roboto, sans-serif; padding: 32px; width: 595px; min-height: 842px; box-sizing: border-box; background: white; color: #111827;">

      <!-- Header -->
      <div style="display: flex; align-items: flex-start; gap: 14px; padding-bottom: 14px; border-bottom: 2px solid #059669;">
        <div>${agentLogoHtml}</div>
        <div style="flex: 1;">
          <div style="font-size: 16px; font-weight: bold;">${offer.agentTradingName || offer.agentName}</div>
          ${offer.agentTradingName ? `<div style="font-size: 11px; color: #6B7280;">${offer.agentName}</div>` : ''}
          ${offer.agentCnpj ? `<div style="font-size: 11px; color: #6B7280;">CNPJ: ${offer.agentCnpj}</div>` : ''}
          ${(offer.agentCompletedCount || 0) > 0 || (offer.agentReviewCount || 0) > 0 ? `
            <div style="font-size: 10px; color: #047857; margin-top: 4px;">
              ${offer.agentCompletedCount ? `<strong>${offer.agentCompletedCount}</strong> processos realizados` : ''}
              ${offer.agentCompletedCount && offer.agentReviewCount ? ' • ' : ''}
              ${offer.agentReviewCount ? `★ ${(offer.agentAvgRating || 0).toFixed(1)} (${offer.agentReviewCount})` : ''}
            </div>
          ` : ''}
        </div>
        <div style="text-align: right;">
          <div style="font-size: 10px; color: #6B7280; letter-spacing: 0.5px;">PROPOSTA COMERCIAL</div>
          <div style="font-size: 18px; font-weight: bold; color: #059669; margin-top: 4px;">#${offer.id.slice(0, 8).toUpperCase()}</div>
          <div style="font-size: 10px; color: #374151; margin-top: 4px;">Cotação: <strong>${offer.quoteTxControl}</strong></div>
          <div style="font-size: 10px; color: #6B7280; margin-top: 2px;">Emissão: ${formatDate(offer.createdAt)}</div>
        </div>
      </div>

      <!-- Client / Validity row -->
      <div style="display: flex; gap: 12px; margin-top: 14px;">
        <div style="flex: 1; background: #F9FAFB; padding: 10px 12px; border-radius: 6px;">
          <div style="font-size: 9px; color: #6B7280; letter-spacing: 0.5px;">CLIENTE</div>
          <div style="font-size: 12px; font-weight: 600; margin-top: 2px;">${offer.shipperTradingName || offer.shipperName || '-'}</div>
          ${offer.shipperCnpj ? `<div style="font-size: 10px; color: #6B7280;">CNPJ: ${offer.shipperCnpj}</div>` : ''}
        </div>
        <div style="flex: 1; background: #FEF3C7; padding: 10px 12px; border-radius: 6px;">
          <div style="font-size: 9px; color: #92400E; letter-spacing: 0.5px;">VALIDADE</div>
          <div style="font-size: 12px; font-weight: 600; color: #78350F; margin-top: 2px;">${formatDate(offer.validityDate)}</div>
        </div>
        ${offer.deliveryTime ? `
        <div style="flex: 1; background: #DBEAFE; padding: 10px 12px; border-radius: 6px;">
          <div style="font-size: 9px; color: #1E40AF; letter-spacing: 0.5px;">TRÂNSITO</div>
          <div style="font-size: 12px; font-weight: 600; color: #1E3A8A; margin-top: 2px;">${offer.deliveryTime} dias</div>
        </div>
        ` : ''}
      </div>

      <!-- Route -->
      <div style="margin-top: 16px;">
        <div style="font-size: 11px; color: #6B7280; margin-bottom: 6px; font-weight: 600;">ROTA</div>
        <div style="display: flex; gap: 10px;">
          <div style="flex: 1; background: #F9FAFB; padding: 10px; border-radius: 6px;">
            <div style="font-size: 9px; color: #6B7280;">Origem</div>
            <div style="font-size: 12px; font-weight: 600; margin-top: 2px;">${formatLocation(offer.origin?.location)}</div>
          </div>
          <div style="flex: 1; background: #F9FAFB; padding: 10px; border-radius: 6px;">
            <div style="font-size: 9px; color: #6B7280;">Destino</div>
            <div style="font-size: 12px; font-weight: 600; margin-top: 2px;">${formatLocation(offer.destination?.location)}</div>
          </div>
        </div>
      </div>

      <!-- Modality chips -->
      ${modalityChips.length > 0 ? `
      <div style="margin-top: 12px; display: flex; flex-wrap: wrap; gap: 6px;">
        ${modalityChips.map(c => `
          <div style="background: #ECFDF5; color: #047857; padding: 4px 10px; border-radius: 4px; font-size: 10px; font-weight: 600; border: 1px solid #D1FAE5;">${c}</div>
        `).join('')}
      </div>
      ` : ''}

      <!-- Cargo description / NCM -->
      ${(offer.goodsDescription || ncms) ? `
      <div style="margin-top: 12px; padding: 10px; background: #F9FAFB; border-radius: 6px; font-size: 11px;">
        ${offer.goodsDescription ? `<div><span style="color: #6B7280;">Mercadoria:</span> <strong>${offer.goodsDescription}</strong></div>` : ''}
        ${ncms ? `<div style="margin-top: 4px;"><span style="color: #6B7280;">NCM:</span> <span style="font-family: monospace;">${ncms}</span></div>` : ''}
      </div>
      ` : ''}

      ${volumesHtml}
      ${routingHtml}
      ${breakdownHtml}
      ${totalsHtml}

      ${offer.paymentTerms ? `
      <div style="margin-top: 14px; font-size: 11px;">
        <span style="color: #6B7280;">Condições de pagamento:</span>
        <strong style="margin-left: 6px;">${offer.paymentTerms}</strong>
      </div>
      ` : ''}

      ${conditionsHtml}

      ${offer.notes ? `
      <div style="margin-top: 14px;">
        <div style="font-size: 11px; color: #6B7280; margin-bottom: 4px; font-weight: 600;">OBSERVAÇÕES</div>
        <div style="font-size: 10px; color: #4B5563; white-space: pre-wrap; line-height: 1.5;">${offer.notes}</div>
      </div>
      ` : ''}

      <!-- Footer -->
      <div style="margin-top: 22px; padding-top: 10px; border-top: 1px solid #E5E7EB; display: flex; justify-content: space-between; align-items: center;">
        <div style="font-size: 9px; color: #9CA3AF;">Gerado em ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}</div>
        <div style="font-size: 9px; color: #059669; font-weight: 600;">Powered by eximtech.com.br</div>
      </div>
    </div>
  `
}

export async function generateOfferPdfBlob(offer: OfferExportData): Promise<{ url: string; fileName: string }> {
  const fileName = `proposta_${offer.quoteTxControl || offer.id.slice(0, 8)}_${(offer.agentTradingName || offer.agentName || '').replace(/[^a-z0-9]+/gi, '_')}.pdf`
  const logo = offer.agentLogoUrl ? await imageToBase64(offer.agentLogoUrl) : null

  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  container.style.top = '0'
  container.innerHTML = createPdfHtml(offer, logo)
  document.body.appendChild(container)

  try {
    const canvas = await html2canvas(container.firstElementChild as HTMLElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    })
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const imgWidth = 210
    const pageHeight = 297 // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width
    const imgData = canvas.toDataURL('image/png')

    // Slice the tall canvas across A4 pages so nothing (e.g. the fee table)
    // gets clipped. Each page shows a different vertical slice by shifting the
    // full image up by one page height.
    let heightLeft = imgHeight
    let position = 0
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight
    while (heightLeft > 0) {
      position -= pageHeight
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    const blob = pdf.output('blob')
    const url = URL.createObjectURL(blob)
    return { url, fileName }
  } finally {
    document.body.removeChild(container)
  }
}

export async function generateOfferPdf(offer: OfferExportData): Promise<void> {
  const { url, fileName } = await generateOfferPdfBlob(offer)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
